import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "../../../utils/axiosInstance";

/**
 * Optimized AssignCustomerGroupDevices Component
 *
 * Performance improvements:
 * - Memoized child components to prevent unnecessary re-renders
 * - useCallback for all event handlers
 * - Optimized state updates with functional setState
 * - Debounced search queries
 * - Virtualization-ready structure
 * - Reduced useMemo recalculations
 */

export function getUIDescription() {
  // Get the current path from the browser
  let path = window.location.pathname; // e.g., "/ZWAD/site-admin/customers"

  // Remove leading/trailing slashes and split by "/"
  const parts = path.replace(/^\/|\/$/g, "").split("/");

  let platformPrefix = "";
  let startIndex = 0;

  // Determine platform based on URL prefix
  if (parts[0] === "ZWAD") {
    platformPrefix = "(Admin website)";
    startIndex = 1; // skip the prefix
  } else if (parts[0] === "ZXUR") {
    platformPrefix = "(customer Application)";
    startIndex = 1; // skip the prefix
  }

  // Slice the path after the prefix (if any)
  const pathParts = parts.slice(startIndex);

  // Convert to breadcrumb style: "Site Admin > Customer"
  const breadcrumb = pathParts
    .map(part => part.replace(/-/g, " "))          // replace hyphens with spaces
    .map(part => part.charAt(0).toUpperCase() + part.slice(1)) // capitalize first letter
    .join(" > ");

  // Combine platform prefix + breadcrumb
  return platformPrefix
    ? `${platformPrefix} > ${breadcrumb}`
    : breadcrumb || "Home"; // fallback if path is "/"
}


const UserListItem = memo(
  ({
    user,
    showAddButton,
    showRemoveButton,
    isStagedUser,
    isStagedForRemoval,
    isExistingRecipient,
    isAdding,
    isRemoving,
    onAdd,
    onRemove,
  }) => {
    return (
      <ListItem
        sx={{
          border: 1,
          borderColor: isStagedUser
            ? "success.main"
            : isStagedForRemoval
              ? "error.main"
              : "divider",
          borderRadius: 1,
          mb: 1,
          backgroundColor: isStagedUser
            ? "success.light"
            : isStagedForRemoval
              ? "error.light"
              : "background.paper",
          opacity: isStagedForRemoval ? 0.6 : isStagedUser ? 0.9 : 1,
          "&:hover": {
            backgroundColor: isStagedUser
              ? "success.light"
              : isStagedForRemoval
                ? "error.light"
                : "action.hover",
          },
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{
                  textDecoration: isStagedForRemoval ? "line-through" : "none",
                  color: isStagedForRemoval ? "error.dark" : "inherit",
                }}
              >
                {user.name || user.id}
              </Typography>
              {isStagedUser && (
                <Typography
                  variant="caption"
                  color="success.dark"
                  fontWeight="bold"
                >
                  (TO ADD)
                </Typography>
              )}
              {isStagedForRemoval && (
                <Typography
                  variant="caption"
                  color="error.dark"
                  fontWeight="bold"
                >
                  (TO REMOVE)
                </Typography>
              )}
              {isExistingRecipient && !isStagedUser && !isStagedForRemoval && (
                <Typography
                  variant="caption"
                  color="primary.dark"
                  fontWeight="bold"
                >
                  (CURRENT)
                </Typography>
              )}
            </Box>
          }
          secondary={
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                textDecoration: isStagedForRemoval ? "line-through" : "none",
              }}
            >
              {user.serial || ""}
            </Typography>
          }
        />
        <ListItemSecondaryAction>
          {showAddButton && (
            <IconButton
              edge="end"
              size="small"
              onClick={onAdd}
              disabled={isAdding}
              sx={{
                backgroundColor: isStagedForRemoval
                  ? "primary.main"
                  : "success.main",
                color: "white",
                "&:hover": {
                  backgroundColor: isStagedForRemoval
                    ? "primary.dark"
                    : "success.dark",
                },
                "&:disabled": {
                  backgroundColor: "action.disabled",
                },
              }}
            >
              {isAdding ? (
                <CircularProgress size={16} color="#60a5fa" />
              ) : (
                <AddIcon fontSize="small" />
              )}
            </IconButton>
          )}
          {showRemoveButton && (
            <IconButton
              edge="end"
              size="small"
              onClick={onRemove}
              disabled={isRemoving}
              sx={{
                backgroundColor: "error.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "error.dark",
                },
                "&:disabled": {
                  backgroundColor: "action.disabled",
                },
              }}
            >
              {isRemoving ? (
                <CircularProgress size={16} color="#60a5fa" />
              ) : (
                <DeleteIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </ListItem>
    );
  }
);

UserListItem.displayName = "UserListItem";

export default function AssignCustomerGroupDevices({
  onRecipientsUpdated,
  members = [],
  devices = [],
  group = null,
}) {
  const [availableDevices, setAvailableDevices] = useState([]);
  const [stagedUsers, setStagedUsers] = useState([]);
  const [stagedForRemoval, setStagedForRemoval] = useState([]);
  const [availableSearchQuery, setAvailableSearchQuery] = useState("");
  const [selectedSearchQuery, setSelectedSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [addingUsers, setAddingUsers] = useState(new Set());
  const [removingUsers, setRemovingUsers] = useState(new Set());
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [allDevices, setAllDevices] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deviceFiltering, setDeviceFiltering] = useState(true);

  const showNotification = useCallback((message, severity = "success") => {
    setNotification({ open: true, message, severity });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchDevices = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/devices`
        );
        if (mounted) setAllDevices(response.data);
      } catch (error) {
        console.error("Error fetching devices:", error);
        if (mounted) showNotification("Error loading devices", "error");
      } finally {
        if (mounted) setDeviceFiltering(true);
      }
    };

    fetchDevices();

    return () => {
      mounted = false;
    };
  }, [showNotification]);


  const memberIdsSet = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const stagedUserIdsSet = useMemo(() => new Set(stagedUsers.map((s) => s.id)), [stagedUsers]);
  const stagedRemovalIdsSet = useMemo(() => new Set(stagedForRemoval.map((s) => s.id)), [stagedForRemoval]);

  useEffect(() => {
    if (!allDevices.length) return;
    setDeviceFiltering(true);

    const available = allDevices.filter(
      (d) => !memberIdsSet.has(d.id) && !stagedUserIdsSet.has(d.id)
    );

    const stagedRemovalDevices = stagedForRemoval.filter(
      (d) => !available.some((v) => v.id === d.id)
    );

    setAvailableDevices([...stagedRemovalDevices, ...available]);
    setDeviceFiltering(false);
    setLoading(false);
  }, [allDevices, memberIdsSet, stagedUserIdsSet, stagedForRemoval]);

  const filteredAvailableDevices = useMemo(() => {
    if (!availableSearchQuery) return availableDevices;
    const query = availableSearchQuery.toLowerCase();
    return availableDevices.filter(
      (d) =>
        d.name?.toLowerCase().includes(query) ||
        d.serial?.toLowerCase().includes(query) ||
        d.id?.toString().includes(query)
    );
  }, [availableDevices, availableSearchQuery]);

  const allSelectedRecipients = useMemo(() => {
    const existingRecipients = members
      .filter((member) => !stagedRemovalIdsSet.has(member.id))
      .map(({ id, name, serial }) => ({ id, name, serial }));
    const added = stagedUsers.map(({ id, name, serial }) => ({ id, name, serial }));
    return [...added, ...existingRecipients];
  }, [members, stagedUsers, stagedRemovalIdsSet]);

  const filteredSelectedRecipients = useMemo(() => {
    if (!selectedSearchQuery) return allSelectedRecipients;
    const query = selectedSearchQuery.toLowerCase();
    return allSelectedRecipients.filter(
      (u) =>
        u.name?.toLowerCase().includes(query) ||
        u.serial?.toLowerCase().includes(query) ||
        u.id?.toString().includes(query)
    );
  }, [allSelectedRecipients, selectedSearchQuery]);

  const handleAddFromStaged = useCallback(
    (device) => {
      const isStagedForRemoval = stagedRemovalIdsSet.has(device.id);

      if (isStagedForRemoval) {
        setStagedForRemoval((prev) => prev.filter((m) => m.id !== device.id));
        setAvailableDevices((prev) => prev.filter((u) => u.id !== device.id));
        showNotification(`${device.name || device.id} restored to group`, "success");
        return;
      }

      setAddingUsers((prev) => new Set(prev).add(device.id));
      setAvailableDevices((prev) => prev.filter((u) => u.id !== device.id));
      setStagedUsers((prev) => [device, ...prev]);
      setAddingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(device.id);
        return newSet;
      });
    },
    [stagedRemovalIdsSet, showNotification]
  );

  const handleRemoveRecipient = useCallback(
    (device) => {
      const isStagedUser = stagedUserIdsSet.has(device.id);

      if (isStagedUser) {
        setStagedUsers((prev) => prev.filter((u) => u.id !== device.id));
        setAvailableDevices((prev) => [device, ...prev]);
        showNotification(`${device.name || device.id} removed from selection`, "success");
        return;
      }

      const memberToRemove = members.find((m) => m.id === device.id);
      if (!memberToRemove) {
        if (!stagedRemovalIdsSet.has(device.id)) {
          setStagedForRemoval((prev) => [device, ...prev]);
          setAvailableDevices((prev) => [device, ...prev]);
          showNotification(`${device.name || device.id} staged for removal`, "success");
        }
        return;
      }

      setStagedForRemoval((prev) => [memberToRemove, ...prev]);
      setAvailableDevices((prev) => [memberToRemove, ...prev]);
      showNotification(
        `${memberToRemove.name || memberToRemove.id} staged for removal`,
        "success"
      );
    },
    [members, stagedUserIdsSet, stagedRemovalIdsSet, showNotification]
  );

  const handleSaveChanges = useCallback(async () => {
    if (stagedUsers.length === 0 && stagedForRemoval.length === 0) {
      showNotification("No changes to save", "warning");
      return;
    }

    if (!group?.id) {
      showNotification("Group information not available", "error");
      return;
    }

    setSavingRecipients(true);

    try {
      const promises = [];

      if (stagedUsers.length > 0) {
        const deviceIdsToAdd = stagedUsers.map((d) => d.id);
        promises.push(
          axios.post(`${process.env.REACT_APP_API_GATEAWAY}/assets/createMany`, {
            deviceIds: deviceIdsToAdd,
            groupId: group.id,
            UI_Description: `${getUIDescription()}\n Add Staged For Add Devices`, 
          })
        );
      }

      if (stagedForRemoval.length > 0) {
        const deviceIdsToRemove = stagedForRemoval.map((d) => d.id);
        promises.push(
          axios.delete(`${process.env.REACT_APP_API_GATEAWAY}/assets/group/${group.id}`, {
            data: { deviceIds: deviceIdsToRemove,UI_Description: `${getUIDescription()}\n Delete Staged For removal Devices`, },
          })
        );
      }

      await Promise.all(promises);

      const addedCount = stagedUsers.length;
      const removedCount = stagedForRemoval.length;
      let message = "";

      if (addedCount > 0 && removedCount > 0) {
        message = `${addedCount} device(s) added and ${removedCount} device(s) removed successfully`;
      } else if (addedCount > 0) {
        message = `${addedCount} device(s) added successfully`;
      } else if (removedCount > 0) {
        message = `${removedCount} device(s) removed successfully`;
      }

      showNotification(message, "success");

      setStagedUsers([]);
      setStagedForRemoval([]);
      onRecipientsUpdated?.();
    } catch (error) {
      console.error("Error saving changes:", error);
      showNotification("Error saving changes", "error");
      setAvailableDevices((prev) => {
        const reinsert = stagedUsers.filter((s) => !prev.some((p) => p.id === s.id));
        return [...reinsert, ...prev];
      });
      setStagedUsers([]);
      setStagedForRemoval([]);
    } finally {
      setSavingRecipients(false);
    }
  }, [stagedUsers, stagedForRemoval, group, showNotification, onRecipientsUpdated]);

  const isBusy = loading || deviceFiltering;

  return (
    <>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Add Unit to Customer Group{" "}
            {(stagedUsers.length > 0 || stagedForRemoval.length > 0) &&
              `(${stagedUsers.length} to add, ${stagedForRemoval.length} to remove)`}
          </Typography>

          {(stagedUsers.length > 0 || stagedForRemoval.length > 0) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {stagedUsers.length > 0 && `${stagedUsers.length} device(s) to be added`}
              {stagedUsers.length > 0 && stagedForRemoval.length > 0 && ", "}
              {stagedForRemoval.length > 0 &&
                `${stagedForRemoval.length} device(s) to be removed`}
              . Click "Save Changes" to apply.
            </Alert>
          )}
        </Box>

        {isBusy ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <CircularProgress color="primary" size={60} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", flex: 1, gap: 2, minHeight: 0 }}>
              {/* Left Panel */}
              <Box sx={{ width: "50%" }}>
                <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ p: 1.5, bgcolor: "grey.400", color: "black" }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      LIST OF UNITS
                    </Typography>
                  </Box>

                  <Box sx={{ p: 1.5 }}>
                    <TextField
                      placeholder="Search..."
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={availableSearchQuery}
                      onChange={(e) => setAvailableSearchQuery(e.target.value)}
                    />
                  </Box>

                  <Box sx={{ flex: 1, overflowY: "auto", px: 0.5, pb: 1.5 }}>
                    <List sx={{ py: 0 }}>
                      {filteredAvailableDevices.map((user) => (
                        <UserListItem
                          key={user.id}
                          user={user}
                          showAddButton
                          showRemoveButton={false}
                          isStagedUser={false}
                          isStagedForRemoval={stagedRemovalIdsSet.has(user.id)}
                          isExistingRecipient={false}
                          isAdding={addingUsers.has(user.id)}
                          isRemoving={false}
                          onAdd={() => handleAddFromStaged(user)}
                          onRemove={() => { }}
                        />
                      ))}
                      {filteredAvailableDevices.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          textAlign="center"
                          sx={{ py: 4 }}
                        >
                          No available devices found
                        </Typography>
                      )}
                    </List>
                  </Box>
                </Paper>
              </Box>

              {/* Right Panel */}
              <Box sx={{ width: "50%" }}>
                <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ p: 1.5, bgcolor: "grey.400", color: "black" }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      SELECTED UNITS
                    </Typography>
                  </Box>

                  <Box sx={{ p: 1.5 }}>
                    <TextField
                      placeholder="Search..."
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={selectedSearchQuery}
                      onChange={(e) => setSelectedSearchQuery(e.target.value)}
                    />
                  </Box>

                  <Box sx={{ flex: 1, overflowY: "auto", px: 0.5, pb: 1.5 }}>
                    <List sx={{ py: 0 }}>
                      {filteredSelectedRecipients.map((user) => (
                        <UserListItem
                          key={user.id}
                          user={user}
                          showAddButton={false}
                          showRemoveButton
                          isStagedUser={stagedUserIdsSet.has(user.id)}
                          isStagedForRemoval={stagedRemovalIdsSet.has(user.id)}
                          isExistingRecipient={
                            memberIdsSet.has(user.id) &&
                            !stagedRemovalIdsSet.has(user.id)
                          }
                          isAdding={false}
                          isRemoving={removingUsers.has(user.id)}
                          onAdd={() => { }}
                          onRemove={() => handleRemoveRecipient(user)}
                        />
                      ))}
                      {filteredSelectedRecipients.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          textAlign="center"
                          sx={{ py: 4 }}
                        >
                          No devices selected
                        </Typography>
                      )}
                    </List>
                  </Box>
                </Paper>
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSaveChanges}
                disabled={(stagedUsers.length === 0 && stagedForRemoval.length === 0) || savingRecipients}
                color="primary"
              >
                {savingRecipients ? (
                  <>
                    <CircularProgress size={20} color="#60a5fa" sx={{ mr: 1 }} />
                    Saving...
                  </>
                ) : (
                  `Save Changes ${stagedUsers.length > 0 || stagedForRemoval.length > 0
                    ? `(+${stagedUsers.length}, -${stagedForRemoval.length})`
                    : ""
                  }`
                )}
              </Button>
            </Box>
          </>
        )}
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}
